import { useState, useEffect, useCallback, useRef } from 'react';

const getDateRangeBounds = (range, customStart, customEnd) => {
  if (range === 'custom') {
    return {
      startDate: customStart ? new Date(customStart).toISOString() : null,
      endDate: customEnd ? new Date(customEnd).toISOString() : null,
    };
  }
  const end = new Date();
  const start = new Date();
  const days = { '24hr': 1, '7days': 7, '30days': 30, '90days': 90 };
  start.setDate(start.getDate() - (days[range] || 30));
  return { startDate: start.toISOString(), endDate: end.toISOString() };
};

const useProTableState = ({
  fetchData,
  filters: filterDefs = [],
  defaultPageSize = 20,
  defaultDateRange = '30days',
}) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const [search, setSearch] = useState('');

  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [customDates, setCustomDates] = useState({ start: '', end: '' });

  const [filterValues, setFilterValues] = useState(() => {
    const initial = {};
    filterDefs.forEach((f) => {
      if (f.key) initial[f.key] = f.defaultValue || '';
    });
    return initial;
  });

  const setFilterValue = useCallback((key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  }, []);

  const hasDateRange = filterDefs.some((f) => f.type === 'dateRange');

  const fetchRef = useRef(fetchData);
  fetchRef.current = fetchData;

  const doFetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = hasDateRange
        ? getDateRangeBounds(dateRange, customDates.start, customDates.end)
        : { startDate: null, endDate: null };
      const params = {
        page,
        pageSize,
        ...(search && { search }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...Object.fromEntries(
          Object.entries(filterValues).filter(([, v]) => v !== '' && v != null)
        ),
      };
      const result = await fetchRef.current(params);
      setData(Array.isArray(result.data) ? result.data : []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('ProTable fetch error:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to load data');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, dateRange, customDates, filterValues, hasDateRange]);

  useEffect(() => { doFetch(); }, [doFetch]);

  const handleSearch = useCallback(() => {
    setPage(0);
    doFetch();
  }, [doFetch]);

  const handleDateRangeChange = useCallback((value) => {
    setDateRange(value);
    setPage(0);
  }, []);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(0);
  }, []);

  return {
    data, total, loading, error,
    page, pageSize, setPage, setPageSize: handlePageSizeChange,
    filterValues, setFilterValue,
    dateRange, setDateRange: handleDateRangeChange, customDates, setCustomDates,
    search, setSearch,
    refresh: doFetch, handleSearch,
  };
};

export default useProTableState;
